/**
 * Formatting utilities for durations and time display
 */

import type { SpanStatisticsBaselineWindow } from "../api/client";

/**
 * Parses a duration string to milliseconds.
 * Supports: "100", "100ms", "1s", "1.5s", "100us", "100µs"
 *
 * @param input - Duration string
 * @returns Milliseconds or null if invalid
 */
export function parseDuration(input: string): number | null {
  const s = input.trim();
  if (!s) return null;
  const match = s.match(/^(\d+(?:\.\d+)?)\s*(ms|s|us|µs|μs)?$/i);
  if (!match) return null;
  const value = parseFloat(match[1]);
  if (Number.isNaN(value) || value < 0) return null;
  const unit = (match[2] ?? "ms").toLowerCase();
  switch (unit) {
    case "s":
      return value * 1000;
    case "us":
    case "µs":
    case "μs":
      return value / 1000;
    default:
      return value;
  }
}

/**
 * Formats a duration in milliseconds to a human-readable string.
 *
 * @param ms - Duration in milliseconds
 * @returns Formatted string (e.g., "1.23ms", "2.45s", "123.45µs")
 */
export function formatDuration(ms: number): string {
  if (ms < 1) {
    return `${(ms * 1000).toFixed(2)}µs`;
  }
  if (ms < 1000) {
    return `${ms.toFixed(2)} ms`;
  }
  return `${(ms / 1000).toFixed(2)} s`;
}

/**
 * Formats a timestamp in milliseconds to an ISO string with space separator.
 *
 * @param ms - Timestamp in milliseconds
 * @returns Formatted ISO string (e.g., "2024-01-01 12:00:00.000")
 */
export function formatTime(ms: number): string {
  const date = new Date(ms);
  return date.toISOString().replace("T", " ").slice(0, -1);
}

export function formatSpanKind(kind: number): string {
  const kinds = ["UNSPECIFIED", "INTERNAL", "SERVER", "CLIENT", "PRODUCER", "CONSUMER"];
  return kinds[kind] ?? "UNKNOWN";
}

export function formatStatusCode(code: number): string {
  const codes = ["UNSET", "OK", "ERROR"];
  return codes[code] ?? "UNKNOWN";
}

export function formatBaselineWindow(window: SpanStatisticsBaselineWindow): string {
  switch (window) {
    case "1h":
      return "1 hour";
    case "24h":
      return "24 hours";
    case "7d":
      return "7 days";
    case "30d":
      return "30 days";
  }
}

export function formatLogMessage(message: string, obj: Record<string, unknown>): string {
  return message.replace(/{\s*(\w+)\s*}/g, (match, p1) => {
    if (!obj[p1]) {
      return `{${p1} = (missing)}`;
    }

    if (typeof obj[p1] === "number" || typeof obj[p1] === "boolean" || typeof obj[p1] === "string") {
      return `{${p1} = ${obj[p1]}}`;
    }

    if (typeof obj[p1] === "object") {
      return `{${p1} = {...}}`;
    }

    return `{${p1} = (unknown)}`;
  });
}

export function formatNumber(num: number): string {
  if (num === 0 || num < 1) return num.toFixed(0);
  const k = 1000;
  const sizes = ["", "K", "M", "B", "T"];
  const i = Math.floor(Math.log(num) / Math.log(k));
  const clampedI = Math.min(i, sizes.length - 1);

  return `${Math.round(num / Math.pow(k, clampedI))}${sizes[clampedI]}`;
}


/**
 * Formats bytes to human-readable format (B, MB, GB, TB)
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const clampedI = Math.min(i, sizes.length - 1);
  const value = bytes / Math.pow(k, clampedI);

  return `${Math.round(value)} ${sizes[clampedI]}`;
}

/**
 * Formats seconds to human-readable format (μs, ms, s, m)
 */
export function formatSeconds(seconds: number): string {
  if (seconds < 0.000001) {
    return `${(seconds * 1000000000).toFixed(0)}ns`;
  }
  if (seconds < 0.001) {
    return `${(seconds * 1000000).toFixed(0)}μs`;
  }
  if (seconds < 1) {
    return `${(seconds * 1000).toFixed(0)}ms`;
  }
  if (seconds < 60) {
    return `${seconds.toFixed(0)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
}

/**
 * Formats a value based on unit type
 */
export function formatValueByUnit(value: number, unit: string | undefined): string {
  if (!unit) return value.toFixed(2);
  
  // Handle "by" unit (bytes)
  if (unit === "by" || unit === "By") {
    return formatBytes(value);
  }
  
  // Handle "s" unit (seconds)
  if (unit === "s") {
    return formatSeconds(value);
  }
  
  // Default: return with 2 decimal places
  return formatNumber(value);
}

export function getNumberMagnitude(maxValue: number): { factor: number; unit: string } {
  if (maxValue === 0 || maxValue < 1) return { factor: 1, unit: "" };
  const k = 1000;
  const sizes = ["", "K", "M", "B", "T"];
  const i = Math.floor(Math.log(maxValue) / Math.log(k));
  const clampedI = Math.max(0, Math.min(i, sizes.length - 1));
  return { factor: Math.pow(k, clampedI), unit: sizes[clampedI] };
}

/**
 * Determines the highest magnitude unit for bytes and returns normalization info
 */
export function getBytesMagnitude(maxValue: number): { factor: number; unit: string } {
  if (maxValue === 0 || maxValue < 1) return { factor: 1, unit: "B" };
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(maxValue) / Math.log(k));
  const clampedI = Math.max(0, Math.min(i, sizes.length - 1));
  return { factor: Math.pow(k, clampedI), unit: sizes[clampedI] };
}

/**
 * Determines the highest magnitude unit for seconds and returns normalization info
 */
export function getSecondsMagnitude(maxValue: number): { factor: number; unit: string } {
  if (maxValue < 0.000001) {
    return { factor: 0.000000001, unit: "ns" };
  }
  if (maxValue < 0.001) {
    return { factor: 0.000001, unit: "μs" };
  }
  if (maxValue < 1) {
    return { factor: 0.001, unit: "ms" };
  }
  if (maxValue < 60) {
    return { factor: 1, unit: "s" };
  }
  return { factor: 60, unit: "m" };
}

/**
 * Formats a value for Y-axis ticks (shows only numbers, no units)
 */
export function formatValueForAxis(value: number): string {
  // Round to integers for cleaner display
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < 0.01) {
    return rounded.toString();
  }
  // Show 1 decimal if needed
  return value.toFixed(1);
}
